import { Head } from '@inertiajs/react'
import { Card, Progress, Alert, Typography, Row, Col, Statistic, Space, Divider } from 'antd'
import { FireOutlined, ClockCircleOutlined, RiseOutlined, FallOutlined } from '@ant-design/icons'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import type { PersonalInsights } from '../types/stats'

const { Title, Paragraph, Text } = Typography

const COLORS = ['#FF4D4F', '#40A9FF', '#52C41A', '#FAAD14', '#722ED1', '#13C2C2', '#F759AB']

const transformDailyDistribution = (dailyData: Record<string, number>) => {
  const dayOrder = ['Monday', '星期一', 'Tuesday', '星期二', 'Wednesday', '星期三', 'Thursday', '星期四', 'Friday', '星期五', 'Saturday', '星期六', 'Sunday', '星期日']
  const isEnglish = Object.keys(dailyData).some((key) => key === 'Monday' || key === 'Tuesday')
  const days = isEnglish ? dayOrder.filter((d) => d.length > 2) : dayOrder.filter((d) => d.length <= 2)
  return days.map((day) => ({ name: day, pomodoros: dailyData[day] || 0 }))
}

const transformHourlyDistribution = (hourlyData: Record<string, number>) =>
  Array.from({ length: 24 }, (_, i) => ({ hour: i, pomodoros: hourlyData[i] || 0, timeLabel: `${i}:00` }))

const transformCategoryDistribution = (categoryData: Record<string, number>) =>
  Object.entries(categoryData).map(([name, value], index) => ({ name, value, fill: COLORS[index % COLORS.length] }))

export default function Insights({ insights }: { insights: PersonalInsights }) {
  const dailyData = transformDailyDistribution(insights.distributions.daily)
  const hourlyData = transformHourlyDistribution(insights.distributions.hourly)
  const categoryData = transformCategoryDistribution(insights.distributions.category)
  const { summary } = insights

  return (
    <div className="p-4 md:p-8">
      <Head title="数据洞察" />
      <Title level={2} className="mb-6 text-center">个人数据洞察</Title>

      <Card className="mb-6">
        <Title level={4}>今日进度</Title>
        <Row gutter={[16, 16]} align="middle">
          <Col span={24} md={12}>
            <Progress
              type="circle"
              percent={summary.today_progress_percentage}
              format={() => `${summary.today_progress}/${summary.daily_target}`}
              status={summary.today_progress >= summary.daily_target ? 'success' : 'active'}
              size={180}
            />
          </Col>
          <Col span={24} md={12}>
            <Space direction="vertical" size="large" className="w-full">
              <Paragraph><Text strong>今日目标: </Text><Text>{summary.daily_target} 个番茄钟</Text></Paragraph>
              <Paragraph><Text strong>已完成: </Text><Text>{summary.today_progress} 个番茄钟 ({summary.today_progress_percentage}%)</Text></Paragraph>
              {summary.today_progress >= summary.daily_target ? (
                <Alert message="恭喜！您已经达成今日目标。" type="success" showIcon />
              ) : (
                <Alert message={`还需要完成 ${summary.daily_target - summary.today_progress} 个番茄钟来达成今日目标。`} type="info" showIcon />
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]} className="mb-6">
        <Col span={24} md={8}>
          <Card>
            <Statistic title="连续专注天数" value={summary.current_streak} suffix="天" prefix={<FireOutlined style={{ color: '#ff4d4f' }} />} />
            <Paragraph className="mt-2 text-gray-500">历史最长: {summary.longest_streak} 天</Paragraph>
          </Card>
        </Col>
        <Col span={24} md={8}>
          <Card>
            <Statistic title="30天内总专注时间" value={Math.round(summary.total_focus_time / 60)} suffix="小时" prefix={<ClockCircleOutlined style={{ color: '#52c41a' }} />} />
            <Paragraph className="mt-2 text-gray-500">完成番茄钟: {summary.total_completed} 个</Paragraph>
          </Card>
        </Col>
        <Col span={24} md={8}>
          <Card>
            <Statistic
              title="周同比"
              value={summary.weekly_trend_percentage}
              precision={1}
              valueStyle={{ color: summary.weekly_trend_percentage >= 0 ? '#3f8600' : '#cf1322' }}
              prefix={summary.weekly_trend_percentage >= 0 ? <RiseOutlined /> : <FallOutlined />}
              suffix="%"
            />
            <Paragraph className="mt-2 text-gray-500">与上周相比的专注度变化</Paragraph>
          </Card>
        </Col>
      </Row>

      <Card className="mb-6">
        <Title level={4}>洞察提示</Title>
        <div className="py-2">
          {insights.insights.map((insight, index) => (
            <Alert key={index} message={insight} type="info" showIcon className="mb-3" />
          ))}
        </div>
      </Card>

      <Row gutter={[16, 16]}>
        <Col span={24} lg={12}>
          <Card title="一周内专注分布" className="mb-6">
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="pomodoros" fill="#FF4D4F" name="番茄钟数量" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <Paragraph className="mt-4 text-center">最高效的一天: <Text strong>{summary.most_productive_day}</Text></Paragraph>
          </Card>
        </Col>

        <Col span={24} lg={12}>
          <Card title="一天内专注分布" className="mb-6">
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hourlyData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <XAxis dataKey="timeLabel" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="pomodoros" fill="#40A9FF" name="番茄钟数量" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <Paragraph className="mt-4 text-center">高效时段: <Text strong>{summary.peak_period}</Text> (高峰: {summary.peak_productivity_hour}:00)</Paragraph>
          </Card>
        </Col>

        <Col span={24}>
          <Card title="任务类别分布" className="mb-6">
            <Row gutter={[16, 16]}>
              <Col span={24} md={12}>
                <div style={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={categoryData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {categoryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Col>
              <Col span={24} md={12} className="flex items-center">
                <div>
                  <Paragraph><Text strong>主要专注类别: </Text><Text>{summary.top_category}</Text></Paragraph>
                  <Paragraph><Text strong>活跃日平均番茄钟: </Text><Text>{summary.avg_pomodoros_per_active_day} 个</Text></Paragraph>
                  <Divider />
                  <Paragraph>任务分类可以帮助您了解在哪些领域投入了更多时间，从而更好地平衡工作与学习。</Paragraph>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  )
}
